"use client";

import {
  usePaginatedQuery,
  useQuery,
  type UsePaginatedQueryReturnType,
} from "convex/react";
import type { FunctionArgs } from "convex/server";
import { api } from "@convex/_generated/api";
import { useSearchResultPagination } from "./use-search-result-pagination";

type LegacyArgs = FunctionArgs<
  typeof api.products.marketplaceQueries.listMarketplacePage
>;

export function useMarketplacePagination(
  args: LegacyArgs | "skip",
  listedAfter: number | undefined,
  setPage: (page: number) => void,
) {
  const ready = useQuery(
    api.products.marketplacePage.ready,
    args === "skip" ? "skip" : {},
  );
  const legacy = useQuery(
    api.products.marketplaceQueries.listMarketplacePage,
    ready === false ? args : "skip",
  );
  const cursorArgs =
    args === "skip"
      ? "skip"
      : {
          productTypeSlug: args.productTypeSlug,
          stateId: args.stateId,
          conditions: args.conditions,
          priceMinCents: args.priceMinCents,
          priceMaxCents: args.priceMaxCents,
          specFilters: args.specFilters,
          listedAfter,
          intent: args.intent,
          palletCount: args.palletCount,
        };
  const cursor = usePaginatedQuery(
    api.products.marketplacePage.list,
    ready === true ? cursorArgs : "skip",
    { initialNumItems: 60 },
  );
  const page = args === "skip" ? 1 : args.page;
  useSearchResultPagination({
    hasSearch: ready === true,
    status: cursor.status,
    resultCount: cursor.results.length,
    page,
    pageSize: 60,
    setPage,
    loadMore: cursor.loadMore,
  });
  if (ready !== true) return legacy;
  return visibleMarketplacePage(cursor, page);
}

function visibleMarketplacePage(
  cursor: UsePaginatedQueryReturnType<typeof api.products.marketplacePage.list>,
  page: number,
) {
  if (
    cursor.status === "LoadingFirstPage" ||
    (cursor.results.length < page * 60 && cursor.status !== "Exhausted")
  )
    return undefined;
  return {
    listings: cursor.results.slice((page - 1) * 60, page * 60),
    pagination: {
      page,
      perPage: 60,
      total: cursor.status === "Exhausted" ? cursor.results.length : null,
      isDone:
        cursor.status === "Exhausted" && cursor.results.length <= page * 60,
    },
  };
}

export function useScopeTotal(args: LegacyArgs | "skip", listedAfter: number | undefined) {
  return useQuery(
    api.products.marketplacePage.count,
    args !== "skip" && countsWholeScope(args, listedAfter)
      ? { productTypeSlug: args.productTypeSlug, stateId: args.stateId }
      : "skip",
  );
}

function countsWholeScope(args: LegacyArgs, listedAfter: number | undefined) {
  return (
    args.intent === undefined &&
    listedAfter === undefined &&
    args.priceMinCents === undefined &&
    args.priceMaxCents === undefined &&
    (args.conditions?.length ?? 0) === 0 &&
    (args.specFilters?.length ?? 0) === 0
  );
}
